import * as React from "react";
import Iconify from "./iconify";
import { IconButtonAnimate } from "./animate";
import PropTypes from "prop-types";
import { Button, Box, Typography } from "@mui/material";
import { alpha, styled } from "@mui/material/styles";
import { fDate, getShortDayMonthFromDate, getShortDayNameFromDate } from "src/utils/formatTime";
import { useLocales } from "src/locales";
import { getMondayBasedWeekdayIndex, getWeekStartDateString, isDayInPast, isInPast } from "src/utils/dateUtils";
import dayjs from "dayjs";
import { RESERVATION_CONSTANTS } from "src/config/constants/reservation-constants";
import useResponsive from "src/hooks/useResponsive";


ButtonArraySessionDatePicker.propTypes = {
    date: PropTypes.instanceOf(Date),
    weekAvailability: PropTypes.array,
    changeDate: PropTypes.func,
    weekStart: PropTypes.string,
    setWeekStart: PropTypes.func,
    isLoading: PropTypes.bool,
};

const areDatesOnSameDay = (date1, date2) => {
    return (
        date1.getFullYear() === date2.getFullYear() &&
        date1.getMonth() === date2.getMonth() &&
        date1.getDate() === date2.getDate()
    );
};

export default function ButtonArraySessionDatePicker({
    date,
    weekAvailability,
    changeDate,
    weekStart,
    setWeekStart,
    isLoading,
}) {
    const {
        translate,
        currentLang: { value },
    } = useLocales();

    const isMobile = useResponsive("down", "sm");

    const getDateButtons = () => {
        const indices = Array.from({ length: 7 }, (_, index) => index);

        const datesArray = indices.map((offset) => {
            const newDate = new Date(weekStart);
            newDate.setDate(newDate.getDate() + offset);
            return {
                date: newDate,
                dayName: getShortDayNameFromDate(newDate, value),
                dateName: getShortDayMonthFromDate(newDate, value),
                available: isDayInPast(newDate) ? 0 : weekAvailability.find(a => a.date === dayjs(newDate).format(RESERVATION_CONSTANTS.YEAR_FORMAT))?.availableCapacity ?? 0
            };
        });

        return (
            <Box
                sx={{
                    display: "flex",
                    gap: 1,
                }}
            >
                {datesArray.map((d, index) => {
                    return (
                        <Button
                            style={{
                                width: 64,
                                height: 80,
                                minWidth: 0,
                                flexShrink: 0,   // IMPORTANT

                            }}
                            onClick={() => {
                                changeDate(d.date);
                            }}
                            color={areDatesOnSameDay(d.date, date) ? "success" : "inherit"}
                            sx={{
                                backgroundColor: (t) => {
                                    return areDatesOnSameDay(d.date, date)
                                        ? t.palette.common.white
                                        : alpha("#666666", 0.2);
                                },
                                "&:hover": {
                                    backgroundColor: (t) => {
                                        return areDatesOnSameDay(d.date, date)
                                            ? t.palette.common.white
                                            : alpha("#666666", 0.4);
                                    },
                                },
                                color: areDatesOnSameDay(d.date, date) ? "#252525" : "white",
                            }}
                            key={index}
                            disabled={!d.available}
                        >
                            <Box
                                sx={{
                                    display: "flex",
                                    flexDirection: "column",
                                }}
                            >
                                <Typography variant="caption" sx={{ fontSize: 13 }}>{d.dayName}</Typography>
                                <Typography variant="subtitle1" sx={{ fontSize: 13, mb: 1 }}>{d.dateName}</Typography>
                                <Typography variant="caption" sx={{ fontSize: 8 }}>{d.available ? `${d.available} ${translate('tickets.left')}` : '-'}</Typography>
                            </Box>
                        </Button>
                    );
                })}
            </Box>
        );
    };

    return (
        <Box
            sx={{
                display: "flex",
                alignItems: "center",
                width: isMobile ? "100%" : "auto",
                overflow: "hidden", // prevents layout breaking
            }}
        >
            <IconButtonAnimate
                onClick={() => {
                    setWeekStart(dayjs(weekStart).subtract(1, "week"));
                }}
                disabled={isLoading}
                sx={{ p: isMobile ? 0.5 : 1 }}

            >
                <Iconify icon="ic:round-chevron-left" width={25} sx={{ color: "white" }} />
            </IconButtonAnimate>
            <Box
                sx={{
                    display: "flex",
                    gap: 1,
                    overflowX: "auto",
                    flex: isMobile ? 1 : "unset",                 // take remaining space
                    px: 1,
                    scrollBehavior: "smooth",
                    "&::-webkit-scrollbar": {
                        display: "none",       // hide scrollbar (optional)
                    },
                }}
            >
                {getDateButtons()}

            </Box>
            <IconButtonAnimate
                onClick={() => {
                    setWeekStart(dayjs(weekStart).add(1, "week"));
                }}
                disabled={isLoading}
                sx={{ p: isMobile ? 0.5 : 1 }}
            >
                <Iconify icon="ic:round-chevron-right" width={25} sx={{ color: "white" }} />
            </IconButtonAnimate>
        </Box>
    );
}
